import { useEffect, useRef } from 'react';
import { Dialog, DialogProps } from 'react-aria-components';

import useIsMobile from '../../hooks/ui/useIsMobile';
import Card from '../Card/Card';
import Modal, { ModalProps } from '../Modal/Modal';
import WrapIf from '../WrapIf/WrapIf';

import DialogCloseButton from './components/DialogCloseButton/DialogCloseButton';

import styles from './DialogModal.module.css';

export type DialogModalProps = {
    onOpen?: () => void;
    onCloseRequested?: () => void;
    unstyled?: boolean;
    disableCloseWithEscape?: boolean;
    hideCloseButton?: boolean;
    nonDismissible?: boolean; // Set disableCloseWithEscape and hideCloseButton to true
} & Omit<ModalProps, 'isKeyboardDismissDisabled' | 'onOpenChange'> &
    Pick<DialogProps, 'aria-label' | 'aria-labelledby' | 'role'>;

const DialogModal = ({
    onOpen,
    onCloseRequested,
    isOpen,
    disableCloseWithEscape,
    hideCloseButton,
    nonDismissible,
    children,
    unstyled,
    width = 'regular',
    'aria-label': ariaLabel = 'Dialog',
    'aria-labelledby': ariaLabelledBy,
    role,
    ...modalProps
}: DialogModalProps) => {
    const isMobile = useIsMobile();
    const isDismissible = !nonDismissible;

    const openCallback = useRef(onOpen);

    useEffect(() => {
        openCallback.current = onOpen;
    }, [onOpen]);

    useEffect(() => {
        isOpen && openCallback.current?.();
    }, [isOpen, openCallback]);

    return (
        <Modal
            {...modalProps}
            isOpen={isOpen}
            width={width}
            isKeyboardDismissDisabled={nonDismissible || disableCloseWithEscape}
            isDismissable={isDismissible}
            position={isMobile ? 'bottomCenter' : 'middleCenter'}
            onOpenChange={(isOpen) => {
                if (!isOpen && onCloseRequested) {
                    onCloseRequested();
                }
            }}
        >
            <Dialog
                className={styles.Root}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                role={role}
            >
                <WrapIf
                    condition={!unstyled}
                    wrapper={(children) => <Card>{children}</Card>}
                >
                    {children}
                </WrapIf>

                {!hideCloseButton && isDismissible && (
                    <DialogCloseButton
                        onClick={onCloseRequested}
                        position={isMobile ? 'bottomCenter' : 'topRight'}
                    />
                )}
            </Dialog>
        </Modal>
    );
};

export default DialogModal;
